import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import api from '@/lib/api';
import { Mail, CheckCircle2, ArrowLeft, Info } from 'lucide-react';

/**
 * ForgotPassword
 * --------------
 * Single-field email form. On submit the backend issues a token and emails
 * the user a reset link (always returns 200 — never leaks whether the email
 * exists on file). On success we render a confirmation card with next steps.
 *
 * Email IS the username for this app, so there's no separate "forgot username"
 * page — the explainer card below tells users to search their inbox if they
 * can't remember which email they signed up with.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await api.post('/auth/forgot-password', { email: email.trim() });
            setSubmitted(true);
            toast.success(r.data?.message || 'If that email exists, a reset link is on the way.');
        } catch (err) {
            // Backend always returns 200 to prevent enumeration; only real failures (rate limit, validation) land here.
            const status = err?.response?.status;
            if (status === 429) {
                toast.error('Too many requests. Please wait an hour and try again.');
            } else if (status === 422) {
                toast.error('That doesn\u2019t look like a valid email address.');
            } else {
                toast.error('Something went wrong. Please try again in a moment.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-amber-50 flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 bg-white/60 backdrop-blur">
                <Link to="/" className="inline-flex items-center" data-testid="forgot-home-link"><Logo /></Link>
            </div>
            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Card className="border-slate-200 shadow-lg">
                        <CardHeader>
                            <CardTitle className="font-display text-2xl text-slate-900">
                                {submitted ? 'Check your inbox' : 'Reset your password'}
                            </CardTitle>
                            {!submitted && (
                                <p className="text-sm text-slate-600">
                                    Enter the email you signed up with. We&apos;ll send you a one-time reset link.
                                </p>
                            )}
                        </CardHeader>
                        <CardContent>
                            {submitted ? (
                                <div className="space-y-4" data-testid="forgot-submitted-card">
                                    <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                                        <div className="text-sm text-emerald-900">
                                            If an account exists for <b>{email}</b>, a reset link is now in your inbox.
                                            The link expires in <b>60 minutes</b> and can only be used once.
                                        </div>
                                    </div>
                                    <div className="text-xs text-slate-500 space-y-1">
                                        <p>Didn&apos;t get it?</p>
                                        <ul className="list-disc pl-5 space-y-0.5">
                                            <li>Check your spam / promotions folder</li>
                                            <li>Confirm you typed the same email you signed up with</li>
                                            <li>Wait 60 seconds and try again — sometimes delivery is delayed</li>
                                        </ul>
                                    </div>
                                    <Button asChild variant="outline" className="w-full" data-testid="forgot-back-to-login">
                                        <Link to="/login">
                                            <ArrowLeft className="mr-2 h-4 w-4" /> Back to sign in
                                        </Link>
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-4">
                                    <div>
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            required
                                            placeholder="you@yourcompany.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            autoComplete="email"
                                            autoFocus
                                            data-testid="forgot-email-input"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={loading || !email}
                                        className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                                        data-testid="forgot-submit-button"
                                    >
                                        <Mail className="mr-2 h-4 w-4" />
                                        {loading ? 'Sending\u2026' : 'Email me a reset link'}
                                    </Button>

                                    <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                                        <Info className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
                                        <p className="text-xs text-slate-600">
                                            <b>Don&apos;t remember which email you used?</b> Search your inbox for past
                                            messages from QuotesFlow (welcome email, quote PDFs, billing receipts) &mdash;
                                            we sent them to the address on your account.
                                        </p>
                                    </div>

                                    <p className="text-center text-sm text-slate-600">
                                        Remembered it? <Link to="/login" className="text-slate-900 font-medium underline" data-testid="forgot-to-login-link">Sign in</Link>
                                    </p>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
